/**
 * Segment-based pleasantness scoring.
 *
 * The route is broken into ~200m segments. Each segment is scored on six
 * dimensions using ONLY data we already fetch (Mapbox steps + classes,
 * Overpass POIs in the bbox, Mapbox POI categories, community tags):
 *
 *   green     — parks/greenery near this segment
 *   blue      — water bodies / waterways near this segment
 *   historic  — historic buildings, landmarks, monuments
 *   quiet     — small streets, no motorway/trunk/primary
 *   lively    — cafes/shops/restaurants/landmarks (clustered = bonus)
 *   negative  — motorway/trunk/primary, tunnels, tolls
 *
 * Each segment also reports `evidence` (e.g. "waterfront", "park: Battery
 * Park", "on motorway") that we surface as chips on the route card.
 *
 * Profiles weight the segment dimensions differently. The route score is the
 * distance-weighted average of segment scores.
 */
import type { RawRoute, RouteStep } from "@/api/routing";
import type { OsmFeatures, OsmEnvironment, OsmAreaKind } from "@/api/osm";
import type { PoiHit } from "@/api/places";
import type { GooglePlaceHit, GooglePlaceType } from "@/api/googlePlaces";
import type { CommunityTag } from "@/api/community";
import { recencyWeight } from "@/api/community";
import type { PleasantnessProfile } from "@/utils/profile";
import {
  cumulativeDistances,
  haversine,
  nearestPointOnPolyline,
  type LngLat,
} from "@/utils/polyline";
import { fmtDistance } from "@/utils/units";

export type Dim = "green" | "blue" | "historic" | "quiet" | "lively" | "negative";

export interface Segment {
  /** Polyline coordinates for this segment (start → end). */
  coords: LngLat[];
  /** Length in meters. */
  lengthM: number;
  /** Cumulative meters from route start to segment midpoint (for evidence labels). */
  midAlongM: number;
  /** Per-dimension scores, 0..100. */
  scores: Record<Dim, number>;
  /** Was this segment classified by Mapbox as motorway/trunk/primary? */
  onMajorRoad: boolean;
  /** Names of nearby features that contributed (deduped, capped). */
  hits: {
    parks: string[];
    water: string[];
    historic: string[];
    cafes: string[];
    landmarks: string[];
    /** Highly-rated Google places that hit this segment, by type. */
    google: { name: string; type: GooglePlaceType }[];
  };
  /** Sum of per-type weights from highly-rated Google places near this segment.
   *  Used both for per-dim contribution and for the route-level consistency bonus. */
  googleWeightedHits: number;
  /** Raw OSM environmental score for this segment (signed; can be negative).
   *  Built from the +/- point system in `OSM_BASE_POINTS`, distance-weighted. */
  osmScore: number;
  /** OSM tag kinds that hit this segment (used for evidence + debug). */
  osmHits: OsmAreaKind[];
}

export interface SegmentedRoute {
  route: RawRoute;
  segments: Segment[];
  /** Total length in meters (sum of segment lengths; matches route.distance ±). */
  totalM: number;
  /** Fraction (0..1) of segments with positive *OSM* environmental score.
   *  This is the "continuous pleasantness" signal in the spec — high values
   *  mean the route feels pleasant across most of its length, not just in
   *  one cluster. */
  pleasantSegmentRatio: number;
  /** Sum of weighted Google hits per km of route. Cheap headline density. */
  pleasantDensityPerKm: number;
  /** Average per-segment OSM environmental score, distance-weighted. Signed. */
  osmEnvScore: number;
  /** Fraction of route distance that hits a strongly-negative tag
   *  (motorway/trunk/primary, industrial, parking-heavy). Used for the
   *  "much of the path is on highways" hard penalty. */
  unpleasantDistanceRatio: number;
}

const SEGMENT_M = 200;

/** Project a polyline into ~200m segments with their coords + length. */
export function buildSegments(coords: LngLat[], targetM = SEGMENT_M): { coords: LngLat[]; lengthM: number; midAlongM: number }[] {
  if (coords.length < 2) return [];
  const cum = cumulativeDistances(coords);
  const total = cum[cum.length - 1];
  if (total <= 0) return [];
  const n = Math.max(1, Math.round(total / targetM));
  const segLen = total / n;
  const out: { coords: LngLat[]; lengthM: number; midAlongM: number }[] = [];
  let vi = 0;
  let lastCut: LngLat = coords[0];
  for (let i = 1; i <= n; i++) {
    const target = i * segLen;
    const segCoords: LngLat[] = [lastCut];
    while (vi < coords.length - 1 && cum[vi + 1] < target) {
      vi++;
      segCoords.push(coords[vi]);
    }
    // Interpolate the cut point on the next vertex.
    let cut: LngLat;
    if (vi >= coords.length - 1) {
      cut = coords[coords.length - 1];
    } else {
      const a = coords[vi];
      const b = coords[vi + 1];
      const dist = cum[vi + 1] - cum[vi];
      const t = dist > 0 ? Math.max(0, Math.min(1, (target - cum[vi]) / dist)) : 0;
      cut = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
    if (segCoords[segCoords.length - 1] !== cut) segCoords.push(cut);
    out.push({
      coords: segCoords,
      lengthM: segLen,
      midAlongM: (i - 0.5) * segLen,
    });
    lastCut = cut;
  }
  return out;
}

/**
 * Walk Mapbox steps in order and figure out which step each meter of the
 * route falls under. Used to attribute road class (motorway/trunk/etc.) to
 * each segment cheaply, without re-querying anything.
 */
function buildClassRanges(steps: RouteStep[]): { startM: number; endM: number; classes: string[] }[] {
  const out: { startM: number; endM: number; classes: string[] }[] = [];
  let acc = 0;
  for (const s of steps) {
    out.push({ startM: acc, endM: acc + s.distance, classes: s.classes ?? [] });
    acc += s.distance;
  }
  return out;
}

function classesForRange(ranges: ReturnType<typeof buildClassRanges>, startM: number, endM: number): Set<string> {
  const out = new Set<string>();
  for (const r of ranges) {
    if (r.endM < startM || r.startM > endM) continue;
    for (const c of r.classes) out.add(c);
  }
  return out;
}

/**
 * Approx distance from a POI center to the nearest point on a *segment* (m).
 *
 * `cum` is the segment's own cumulativeDistances. Pass it whenever this runs
 * in a loop (PLE-6): without it nearestPointOnPolyline rebuilds the array on
 * every call. `distance` never reads `cum` (only `along` does), so passing it
 * changes no result.
 */
function distToSegment(p: LngLat, seg: LngLat[], cum?: number[]): number {
  const r = nearestPointOnPolyline(p, seg, cum);
  return r.distance;
}

// ---------------------------------------------------------------------------
// Distance pre-check (PLE-6, Oct 5). Profiled in the Simulator: a superseded
// Maine search froze the app for 471.7 s inside segmentRoute, measuring every
// ~660 ft route segment against every vertex of a 129,954-element Overpass
// result -- about 4.7 billion nearestPointOnPolyline calls, almost all of them
// for features miles from the segment.
//
// This skips a feature only when a LOWER BOUND on its distance already
// exceeds the threshold the caller compares against, so every comparison
// comes out exactly as it did before. It is not a new radius. The bound is
// derived from nearestPointOnPolyline's own math: for a segment of 2+ points
// it measures in a local equirectangular projection (111320 m per degree,
// longitude scaled by cos(point latitude)), and the snapped point always lies
// inside the segment's bounding box. So the projected distance can never be
// less than the latitude gap, or the longitude gap scaled by the smallest
// cos(lat) the point could have, between the two boxes. A 1-point segment
// takes nearestPointOnPolyline's haversine path instead, so it is never
// pre-checked. PREFILTER_SLACK_M absorbs floating-point rounding.
// ---------------------------------------------------------------------------

/** Must match nearestPointOnPolyline's projection constant (polyline.ts). */
const PROJ_M_PER_DEG = 111320;
/** 1 m, about 3 feet -- orders of magnitude above float rounding here. */
const PREFILTER_SLACK_M = 1;

export interface LngLatBox { minLng: number; maxLng: number; minLat: number; maxLat: number }

export function boxOf(coords: LngLat[]): LngLatBox {
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return { minLng, maxLng, minLat, maxLat };
}

/**
 * Lower bound (m) on distToSegment(p, seg) for EVERY point p inside `feature`,
 * where `seg` is the bounding box of a segment with 2+ points. NaN inputs give
 * NaN, which never compares greater, so a malformed feature is never skipped.
 */
export function distanceLowerBoundM(
  feature: LngLatBox,
  seg: LngLatBox,
  /** cosMinOf(feature), when the caller has it cached. Same value either way. */
  cosMin: number = cosMinOf(feature),
): number {
  const latGap = Math.max(0, seg.minLat - feature.maxLat, feature.minLat - seg.maxLat);
  const lngGap = Math.max(0, seg.minLng - feature.maxLng, feature.minLng - seg.maxLng);
  return Math.max(latGap * PROJ_M_PER_DEG, lngGap * PROJ_M_PER_DEG * cosMin);
}

/** Smallest cos(latitude) of any point in `box` -- the longitude scale the
 *  bound may assume. Depends only on the box, so callers cache it. */
export function cosMinOf(box: LngLatBox): number {
  const maxAbsLat = Math.max(Math.abs(box.minLat), Math.abs(box.maxLat));
  return Math.cos((maxAbsLat * Math.PI) / 180);
}

/** The pre-check's own allowance, for callers outside this file. */
export const DISTANCE_PRECHECK_SLACK_M = PREFILTER_SLACK_M;

// ---------------------------------------------------------------------------
// Latitude-strip index (PLE-6, second pass). Profiled again after the bound
// shipped: the bound itself ran ~1.6 billion times on a Maine route, because
// it was still asked once per (route segment x feature). The index lets each
// segment visit only features whose latitude range reaches its own band.
//
// It omits a feature only when its whole latitude range lies outside the
// segment's range padded by (threshold + slack) -- so its latitude gap alone
// already exceeds the threshold and the bound above would have skipped it
// anyway. Candidates come back in ascending original order, so every loop
// visits features in exactly the order it did before.
// ---------------------------------------------------------------------------

/** Strip height: 0.01 degrees of latitude, about 0.7 miles. */
const STRIP_DEG = 0.01;
/** A feature spanning more strips than this (one huge relation) is simply
 *  always visited rather than inserted into thousands of strips. */
const MAX_STRIPS_PER_FEATURE = 2_000;

export interface LatStripIndex {
  boxes: LngLatBox[];
  cosMin: number[];
  strips: Map<number, number[]>;
  /** Visited by every query: unboundable boxes (NaN) and very tall ones. */
  always: number[];
}

export function buildLatStripIndex(
  boxes: LngLatBox[],
  /** Features that must never be omitted (e.g. a 1-point water line). */
  mustVisit?: (i: number) => boolean,
): LatStripIndex {
  const strips = new Map<number, number[]>();
  const always: number[] = [];
  const cosMin: number[] = new Array(boxes.length);
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i];
    cosMin[i] = cosMinOf(b);
    const lo = Math.floor(b.minLat / STRIP_DEG);
    const hi = Math.floor(b.maxLat / STRIP_DEG);
    if (mustVisit?.(i) || !Number.isFinite(lo) || !Number.isFinite(hi) || hi - lo > MAX_STRIPS_PER_FEATURE) {
      always.push(i);
      continue;
    }
    for (let k = lo; k <= hi; k++) {
      const arr = strips.get(k);
      if (arr) arr.push(i);
      else strips.set(k, [i]);
    }
  }
  return { boxes, cosMin, strips, always };
}

/** Indices, ascending, of every feature whose latitude range could come within
 *  `thresholdM` of [minLat, maxLat]. A superset: callers still apply the bound. */
export function latStripCandidates(idx: LatStripIndex, minLat: number, maxLat: number, thresholdM: number): number[] {
  const pad = (thresholdM + PREFILTER_SLACK_M) / PROJ_M_PER_DEG;
  const lo = Math.floor((minLat - pad) / STRIP_DEG);
  const hi = Math.floor((maxLat + pad) / STRIP_DEG);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return idx.boxes.map((_, i) => i);
  const out: number[] = idx.always.slice();
  for (let k = lo; k <= hi; k++) {
    const arr = idx.strips.get(k);
    if (arr) for (const i of arr) out.push(i);
  }
  out.sort((a, b) => a - b);
  let w = 0;
  for (let r = 0; r < out.length; r++) if (r === 0 || out[r] !== out[r - 1]) out[w++] = out[r];
  out.length = w;
  return out;
}

/** Test hook: lets the equivalence test run segmentRoute with and without
 *  the pre-check on the same input. Never switched off in the app. */
let distancePrefilterEnabled = true;
export function __setDistancePrefilterForTests(enabled: boolean): void {
  distancePrefilterEnabled = enabled;
}
/** Read by scoring.ts's water loop so the same hook covers it. */
export function distancePrefilterOn(): boolean {
  return distancePrefilterEnabled;
}
/** Test hook: how many (segment, feature) pairs segmentRoute has visited.
 *  Lets a test assert the work tracks NEARBY features, not segments x all
 *  features -- the shape of the 471.7 s freeze (PLE-6). One increment per
 *  segment per list, so it costs nothing measurable. */
let visitedPairs = 0;
export function __visitedPairsForTests(reset = false): number {
  const n = visitedPairs;
  if (reset) visitedPairs = 0;
  return n;
}

interface BuildArgs {
  route: RawRoute;
  /** Buffer (meters) around segment to count POIs as "hitting" it. */
  buffer: number;
  pois: { cafes: PoiHit[]; shops: PoiHit[]; landmarks: PoiHit[] };
  /** OSM aggregates (bbox-wide). Used as a corridor-level fallback signal. */
  osm: OsmFeatures;
  /** Per-feature OSM points for accurate per-segment scoring. Optional —
   *  if absent we fall back to the bbox-wide OSM densities. */
  osmPoints?: {
    parks: { lng: number; lat: number; name?: string }[];
    water: { lng: number; lat: number; name?: string }[];
    historic: { lng: number; lat: number; name?: string }[];
  };
  /** Rich OSM environment (points + lines for every spec'd tag).
   *  When present, drives the per-segment +/- environmental scoring. */
  osmEnv?: OsmEnvironment;
  /** User profile — controls per-tag multipliers (e.g. Quiet penalizes
   *  highways much harder; Water rewards waterway/coastline more). */
  profile?: PleasantnessProfile;
  community: CommunityTag[];
  /** Highly-rated Google Places (rating>=4.3, reviews>=50) along the route.
   *  We attribute each hit to the segments it falls near and weight it by
   *  type (parks/attractions/landmarks high, café/restaurant light). */
  googlePlaces?: GooglePlaceHit[];
}

/**
 * Per-type contribution to each pleasantness dimension. The intent: a park
 * lifts GREEN strongly; a museum/church/library lifts HISTORIC; a tourist
 * attraction lifts both HISTORIC and (lightly) LIVELY; cafés/restaurants
 * only lift LIVELY a small amount. Values are points added per hit (then
 * scaled by the place's per-type weight).
 */
const GOOGLE_DIM_CONTRIBUTION: Record<GooglePlaceType, Partial<Record<Dim, number>>> = {
  park:               { green: 32, historic: 4 },
  tourist_attraction: { historic: 22, lively: 8, green: 4 },
  museum:             { historic: 26, lively: 4 },
  church:             { historic: 22 },
  library:            { historic: 14, quiet: 6 },
  cafe:               { lively: 6 },
  shop:               { lively: 5 },
  restaurant:         { lively: 5 },
  gas:                {},
  bathroom:           {},
};

// =====================================================================
// OSM environmental scoring
// =====================================================================

/**
 * Base points per OSM tag — exactly the +/- system from the spec. These are
 * applied per segment, then scaled by the user's profile (e.g. "Quiet"
 * triples the highway penalty; "Water" doubles the river/coastline bonus).
 *
 * Tags not in this map contribute 0.
 */
const OSM_BASE_POINTS: Record<OsmAreaKind, number> = {
  // Strong positive — parks & open green spaces are top-priority pleasantness.
  park:              +6,
  garden:            +6,
  nature_reserve:    +7,   // protected nature ranks highest
  recreation_ground: +4,
  farmland:          +4,   // open farm fields — exactly what user asked for
  meadow:            +5,
  orchard:           +4,
  open_green:        +3,
  viewpoint:         +4,
  water:       +5,
  river:       +5,
  canal:       +5,
  coastline:   +5,
  wood:        +4,
  forest:      +4,
  historic:    +3,
  attraction:  +3,
  // Moderate positive
  residential_land: +2,
  residential:      +2,
  living_street:    +2,
  pedestrian:       +2,
  path:             +2,
  cycleway:         +2,
  secondary:        -5,
  tertiary:         -4,
  secondary_link:   -5,
  tertiary_link:    -4,
  no_sidewalk:      -7,
  library:          +1,
  church:           +1,
  // Negative
  industrial:    -5,
  motorway:      -6,
  trunk:         -6,
  primary:       -6,
  motorway_link: -6,
  trunk_link:    -6,
  primary_link:  -6,
  parking:       -3,
  parking_aisle: -3,
  // Dead-end ways (noexit=yes / service=driveway). The loop filter drops
  // routes that traverse these mid-route; if one survives to scoring
  // (e.g. the start/end address's own street), it's a small negative.
  dead_end:      -2,
};

/** Which dimension each tag contributes to, for the existing dim scoring. */
const OSM_DIM: Record<OsmAreaKind, Exclude<Dim, "negative"> | "negative"> = {
  park: "green", garden: "green", wood: "green", forest: "green",
  nature_reserve: "green", recreation_ground: "green",
  farmland: "green", meadow: "green", orchard: "green", open_green: "green",
  viewpoint: "green",
  water: "blue", river: "blue", canal: "blue", coastline: "blue",
  historic: "historic", attraction: "historic", church: "historic", library: "historic",
  residential_land: "quiet", residential: "quiet", living_street: "quiet",
  pedestrian: "quiet", path: "quiet", cycleway: "quiet",
  secondary: "negative", tertiary: "negative",
  secondary_link: "negative", tertiary_link: "negative", no_sidewalk: "negative",
  industrial: "negative",
  motorway: "negative", trunk: "negative", primary: "negative",
  motorway_link: "negative", trunk_link: "negative", primary_link: "negative",
  parking: "negative", parking_aisle: "negative",
  dead_end: "negative",
};

/**
 * Per-profile multipliers on the base OSM points.
 *
 *  - Nature-heavy:  green/water count more, lively counts less, big highway penalty.
 *  - Water views:   water/coast/river MUCH more, big highway penalty.
 *  - Historic:      historic/civic count more, light highway penalty.
 *  - Quiet:         residential/living/path count more, *very* strong highway penalty.
 *  - Lively:        attractions/civic count more, residential less, but still penalize industrial/highway.
 */
type ProfileMultipliers = Partial<Record<OsmAreaKind, number>> & { _default?: number };

const PROFILE_MULTIPLIERS: Record<PleasantnessProfile, ProfileMultipliers> = {
  nature: {
    park: 1.6, garden: 1.6, wood: 1.5, forest: 1.5,
    nature_reserve: 1.8, recreation_ground: 1.3,
    farmland: 1.5, meadow: 1.6, orchard: 1.5, open_green: 1.3,
    viewpoint: 1.4,
    water: 1.2, river: 1.2, canal: 1.2, coastline: 1.2,
    motorway: 1.2, trunk: 1.2, primary: 1.2, industrial: 1.3,
    motorway_link: 1.2, trunk_link: 1.2, primary_link: 1.2,
  },
  water: {
    water: 1.8, river: 1.8, canal: 1.8, coastline: 2.0,
    park: 1.0, garden: 1.0,
    motorway: 1.2, trunk: 1.2, primary: 1.2,
    motorway_link: 1.2, trunk_link: 1.2, primary_link: 1.2,
  },
  historic: {
    historic: 1.6, attraction: 1.4, church: 1.6, library: 1.4,
    residential_land: 1.2, pedestrian: 1.3,
    motorway: 0.7, trunk: 0.7, primary: 0.7,
    motorway_link: 0.7, trunk_link: 0.7, primary_link: 0.7,
  },
  quiet: {
    residential: 1.5, residential_land: 1.4, living_street: 1.6,
    pedestrian: 1.6, path: 1.4, cycleway: 1.3,
    library: 1.2, church: 1.2,
    // Spec: highways/primary roads get a *very strong* penalty here.
    motorway: 2.2, trunk: 2.0, primary: 1.8,
    motorway_link: 2.0, trunk_link: 1.8, primary_link: 1.6,
    secondary: 1.8, tertiary: 1.6, secondary_link: 1.6, tertiary_link: 1.5, no_sidewalk: 2.2,
    industrial: 1.6, parking: 1.4, parking_aisle: 1.4,
  },
  lively: {
    attraction: 1.5, historic: 1.3, library: 1.2, church: 1.1,
    pedestrian: 1.3,
    // Still penalize ugly: industrial + highways.
    motorway: 1.0, trunk: 1.0, primary: 1.0, industrial: 1.2,
    motorway_link: 1.0, trunk_link: 1.0, primary_link: 1.0,
  },
};

function profileMultiplier(profile: PleasantnessProfile | undefined, kind: OsmAreaKind): number {
  if (!profile) return 1;
  return PROFILE_MULTIPLIERS[profile][kind] ?? 1;
}

/**
 * Distance from a point to a polyline (line) in meters, using the segment's
 * own polyline as the query (we want "is this OSM line near this route
 * segment?" — so we test min vertex-to-line distance, both directions).
 */
function lineToSegmentDistance(line: LngLat[], seg: LngLat[], segCum?: number[]): number {
  // Cheap approximation: min distance from any vertex of `line` to the segment
  // polyline. Good enough at our buffer sizes (150–500m).
  let best = Infinity;
  for (const p of line) {
    const d = distToSegment(p, seg, segCum);
    if (d < best) best = d;
    if (best < 5) break;
  }
  return best;
}

/** Build segmented + scored route from existing data only. */
// CAUSAL PROOF FOR THE NaN (Morgan, Aug 22 — diagnostic only, no behaviour
// change). OSM_BASE_POINTS is a Record keyed on OsmAreaKind. A value that is
// not in that union looks up as `undefined`, the `pts === 0` guard below is
// FALSE for undefined, and `pts * mult * falloff` is then NaN — which
// propagates through osmScore -> osmEnvScore -> envBump -> clamp (all of which
// pass NaN through) into the ranking score. This names any such kind the first
// time it is seen, so the fix is applied to a confirmed cause rather than a
// suspected one. Removed once the union hole is closed.
const __seenBadKind = new Set<string>();
function __reportBadKind(where: string, kind: string, pts: unknown): void {
  if (__seenBadKind.has(kind)) return;
  __seenBadKind.add(kind);
  // eslint-disable-next-line no-console
  console.log(
    `[segments] NON-FINITE base points in ${where}: kind=${JSON.stringify(kind)} ` +
    `OSM_BASE_POINTS[kind]=${String(pts)} — this poisons osmScore with NaN`,
  );
}

export function segmentRoute(args: BuildArgs): SegmentedRoute {
  const { route, buffer, pois, osm, osmPoints, community, googlePlaces, osmEnv, profile } = args;
  const rawSegs = buildSegments(route.coordinates);
  const totalM = rawSegs.reduce((s, x) => s + x.lengthM, 0) || route.distance;
  const ranges = buildClassRanges(route.steps);

  // Corridor-level fallbacks (used if we don't have point-level OSM).
  const km = Math.max(0.1, route.distance / 1000);
  const corridorParkDensity = osm.parks / km;       // parks per km of route
  const corridorWaterDensity = osm.waterBodies / km;
  const corridorResidentialPct =
    (osm.residentialKm / Math.max(0.001, osm.residentialKm + osm.highwayKm)) * 100;

  // OSM env: tighter buffer for linear features (roads/water), since the
  // route literally either runs ON them or doesn't. Use the regular buffer
  // for area-like points (parks, historic, etc.).
  const lineBuffer = Math.min(buffer, 60); // ~60m: line is on or alongside route
  const linesByKind = new Map<OsmAreaKind, { line: LngLat[]; name?: string }[]>();
  if (osmEnv) {
    for (const l of osmEnv.lines) {
      const arr = linesByKind.get(l.kind) ?? [];
      arr.push({ line: l.line, name: l.name });
      linesByKind.set(l.kind, arr);
    }
  }

  // Built once per call (PLE-6): each feature's box, cached cos bound and a
  // latitude-strip index, so a segment only visits features in its band.
  const pointIndex = <T,>(items: readonly T[], at: (x: T) => LngLat): LatStripIndex =>
    buildLatStripIndex(items.map((x) => {
      const [lng, lat] = at(x);
      return { minLng: lng, maxLng: lng, minLat: lat, maxLat: lat };
    }));
  const ll = <T extends { lng: number; lat: number }>(x: T): LngLat => [x.lng, x.lat];
  const parksIdx = osmPoints ? pointIndex(osmPoints.parks, ll) : null;
  const waterIdx = osmPoints ? pointIndex(osmPoints.water, ll) : null;
  const historicIdx = osmPoints ? pointIndex(osmPoints.historic, ll) : null;
  const cafesIdx = pointIndex(pois.cafes, (x) => x.center);
  const shopsIdx = pointIndex(pois.shops, (x) => x.center);
  const landmarksIdx = pointIndex(pois.landmarks, (x) => x.center);
  const googleList = googlePlaces ?? [];
  const googleIdx = pointIndex(googleList, (x) => x.center);
  const envPointsIdx = osmEnv ? pointIndex(osmEnv.points, ll) : null;
  const linesIdxByKind = new Map<OsmAreaKind, LatStripIndex>();
  for (const [kind, ls] of linesByKind) linesIdxByKind.set(kind, buildLatStripIndex(ls.map((l) => boxOf(l.line))));

  const segments: Segment[] = rawSegs.map((s) => {
    const startM = s.midAlongM - s.lengthM / 2;
    const endM = s.midAlongM + s.lengthM / 2;
    const classes = classesForRange(ranges, startM, endM);
    const onMotorway = classes.has("motorway");
    const onMajor = onMotorway || classes.has("trunk") || classes.has("primary") || classes.has("secondary") || classes.has("tertiary");
    const onTunnel = classes.has("tunnel");
    const onToll = classes.has("toll");

    // Computed once per segment (PLE-6) -- see distToSegment and the
    // distance pre-check above.
    const segCum = cumulativeDistances(s.coords);
    const segBox = boxOf(s.coords);
    const canPrecheck = distancePrefilterEnabled && s.coords.length >= 2;
    /** Indices, in original order, of the features that could be within
     *  `threshold` of this segment. Every omitted one is provably beyond it.
     *  Without the pre-check: every index, as before. */
    const visit = (idx: LatStripIndex, threshold: number): number[] => {
      if (!canPrecheck) return idx.boxes.map((_, i) => i);
      const candidates = latStripCandidates(idx, segBox.minLat, segBox.maxLat, threshold);
      visitedPairs += candidates.length;
      return candidates.filter(
        (i) => !(distanceLowerBoundM(idx.boxes[i], segBox, idx.cosMin[i]) > threshold + PREFILTER_SLACK_M),
      );
    };

    // ---- Counts within `buffer` of THIS segment ----
    const nearIn = <T,>(items: readonly T[], idx: LatStripIndex, at: (x: T) => LngLat): T[] =>
      visit(idx, buffer).map((i) => items[i]).filter((x) => distToSegment(at(x), s.coords, segCum) <= buffer);

    const nearParks = osmPoints && parksIdx ? nearIn(osmPoints.parks, parksIdx, ll) : [];
    const nearWater = osmPoints && waterIdx ? nearIn(osmPoints.water, waterIdx, ll) : [];
    const nearHistoric = osmPoints && historicIdx ? nearIn(osmPoints.historic, historicIdx, ll) : [];
    const nearCafes = nearIn(pois.cafes, cafesIdx, (x) => x.center);
    const nearShops = nearIn(pois.shops, shopsIdx, (x) => x.center);
    const nearLandmarks = nearIn(pois.landmarks, landmarksIdx, (x) => x.center);
    const nearGoogle = nearIn(googleList, googleIdx, (x) => x.center);

    // ---- Per-dimension segment scoring (0..100) ----
    let green: number;
    if (osmPoints) {
      green = clamp(nearParks.length * 30);
    } else {
      green = clamp(corridorParkDensity * 30);
    }

    let blue: number;
    if (osmPoints) {
      blue = clamp(nearWater.length * 35);
    } else {
      blue = clamp(corridorWaterDensity * 25);
    }

    const histCount = nearHistoric.length + nearLandmarks.length;
    let historic = clamp(histCount * 25);

    const liveCount = nearCafes.length + nearShops.length + nearLandmarks.length;
    let lively = clamp(liveCount * 14);
    if (liveCount >= 3) lively = clamp(lively + 10);

    // ---- Google Places overlay (highly-rated, density-weighted) ----
    let googleWeightedHits = 0;
    if (nearGoogle.length > 0) {
      const dimAdd: Record<Dim, number> = {
        green: 0, blue: 0, historic: 0, quiet: 0, lively: 0, negative: 0,
      };
      for (const g of nearGoogle) {
        const w = g.weight;
        googleWeightedHits += w;
        const contrib = GOOGLE_DIM_CONTRIBUTION[g.type];
        for (const [dim, pts] of Object.entries(contrib) as [Dim, number][]) {
          dimAdd[dim] += pts * w;
        }
      }
      // Diminishing returns so a single cluster can't dominate.
      const compress = (v: number) => v <= 0 ? 0 : v <= 30 ? v : 30 + Math.sqrt(v - 30) * 6;
      green    = clamp(green    + compress(dimAdd.green));
      historic = clamp(historic + compress(dimAdd.historic));
      lively   = clamp(lively   + compress(dimAdd.lively));
    }

    let quiet: number;
    if (classes.size > 0) {
      // Primary roads (US-9, NY-100, NY-22, etc.) are stoplight-heavy
      // commercial arterials — treat them as nearly as hostile as trunks.
      if (onMotorway) quiet = 5;
      else if (classes.has("trunk")) quiet = 10;
      else if (classes.has("primary")) quiet = 12;
      else if (classes.has("secondary")) quiet = 35;
      else if (classes.has("tertiary")) quiet = 50;
      else quiet = 85;
    } else {
      quiet = clamp(corridorResidentialPct * 0.9);
    }

    let negative = 0;
    // Primary roads belong with the highway-like bucket, NOT with secondary
    // collectors. Pleasant mode must avoid them as aggressively as it
    // avoids interstates.
    if (onMotorway) negative += 70;
    else if (classes.has("trunk")) negative += 60;
    else if (classes.has("primary")) negative += 55;
    else if (classes.has("secondary")) negative += 24;
    else if (classes.has("tertiary")) negative += 14;
    if (onToll) negative += 10;
    if (onTunnel) negative += 8;
    negative = clamp(negative);

    // ---- OSM environmental scoring (per spec +/- point system) ----
    // Each OSM tag we hit on this segment contributes its base point value
    // (multiplied by the user's profile weighting). We use distance falloff
    // for points (full credit if very close, 25% credit at the buffer edge),
    // and require linear features to be within `lineBuffer` (tight).
    let osmScore = 0;
    const osmHits: OsmAreaKind[] = [];

    if (osmEnv) {
      // ---- Point features (areas / nodes) ----
      for (const pi of visit(envPointsIdx!, buffer)) {
        const p = osmEnv.points[pi];
        const d = distToSegment([p.lng, p.lat], s.coords, segCum);
        if (d > buffer) continue;
        const pts = OSM_BASE_POINTS[p.kind];
        // GUARD, NOT JUST A REPORT. An off-union kind resolves to undefined
        // here, and `undefined === 0` is false, so the old check waved it
        // through into `pts * mult * falloff` = NaN. Skipping costs one
        // feature's contribution; not skipping poisons the whole route score.
        if (!Number.isFinite(pts)) {
          __reportBadKind("points", String(p.kind), pts);
          continue;
        }
        if (pts === 0) continue;
        const mult = profileMultiplier(profile, p.kind);
        const falloff = falloffFactor(d, buffer);
        const v = pts * mult * falloff;
        osmScore += v;
        osmHits.push(p.kind);
        // Also nudge the matching dimension so existing dim aggregates stay
        // aligned with the OSM signal.
        const dim = OSM_DIM[p.kind];
        if (dim === "negative") {
          negative = clamp(negative + Math.abs(v) * 4); // 1 point ≈ +4 on 0..100
        } else {
          // +1 base point ≈ +6 on 0..100 dim, with diminishing returns above 30.
          const lift = compressLift(v * 6);
          if (dim === "green")    green    = clamp(green    + lift);
          else if (dim === "blue") blue    = clamp(blue    + lift);
          else if (dim === "historic") historic = clamp(historic + lift);
          else if (dim === "quiet")    quiet    = clamp(quiet    + lift);
          else if (dim === "lively")   lively   = clamp(lively   + lift);
        }
      }

      // ---- Linear features (roads, river, canal, coastline) ----
      // For a route segment, "on this road" matters far more than "near it",
      // so we use a tight buffer and full credit if within ~30m.
      for (const [kind, ls] of linesByKind) {
        const pts = OSM_BASE_POINTS[kind];
        // GUARD, NOT JUST A REPORT. An off-union kind resolves to undefined
        // here, and `undefined === 0` is false, so the old check waved it
        // through into `pts * mult * falloff` = NaN. Skipping costs one
        // feature's contribution; not skipping poisons the whole route score.
        if (!Number.isFinite(pts)) {
          __reportBadKind("lines", String(kind), pts);
          continue;
        }
        if (pts === 0) continue;
        let bestD = Infinity;
        // Visit only lines that could be within BOTH numbers the result is
        // compared against: lineBuffer (below) and the 5 m early exit, which
        // an omitted line must not be able to trigger.
        for (const li of visit(linesIdxByKind.get(kind)!, Math.max(lineBuffer, 5))) {
          const l = ls[li];
          const d = lineToSegmentDistance(l.line, s.coords, segCum);
          if (d < bestD) bestD = d;
          if (bestD < 5) break;
        }
        if (!isFinite(bestD) || bestD > lineBuffer) continue;
        const mult = profileMultiplier(profile, kind);
        // Tight falloff for lines: full credit ≤30m, 0 at lineBuffer.
        const falloff = bestD <= 30 ? 1 : Math.max(0, 1 - (bestD - 30) / Math.max(1, lineBuffer - 30));
        const v = pts * mult * falloff;
        osmScore += v;
        osmHits.push(kind);
        const dim = OSM_DIM[kind];
        if (dim === "negative") {
          negative = clamp(negative + Math.abs(v) * 6); // lines hit harder
        } else {
          const lift = compressLift(v * 7);
          if (dim === "green")    green    = clamp(green    + lift);
          else if (dim === "blue") blue    = clamp(blue    + lift);
          else if (dim === "historic") historic = clamp(historic + lift);
          else if (dim === "quiet")    quiet    = clamp(quiet    + lift);
          else if (dim === "lively")   lively   = clamp(lively   + lift);
        }
      }
    }

    // Community signal: nudge dimensions if recent tags hit this segment.
    if (community.length > 0) {
      const localBonus = communityBonus(community, s.coords, buffer);
      green = clamp(green + localBonus.green);
      quiet = clamp(quiet + localBonus.calm - localBonus.stress);
      lively = clamp(lively + localBonus.lively);
      negative = clamp(negative + localBonus.stress * 1.5);
    }

    // Pick the most-named Google hits to surface as evidence (parks &
    // attractions first, café/restaurant last).
    const sortedGoogle = [...nearGoogle].sort((a, b) => b.weight - a.weight);
    const googleNames = sortedGoogle.slice(0, 4).map((g) => ({ name: g.name, type: g.type }));

    return {
      coords: s.coords,
      lengthM: s.lengthM,
      midAlongM: s.midAlongM,
      scores: { green, blue, historic, quiet, lively, negative },
      onMajorRoad: onMajor,
      hits: {
        parks: dedup(nearParks.map((x) => x.name).filter(Boolean) as string[]),
        water: dedup(nearWater.map((x) => x.name).filter(Boolean) as string[]),
        historic: dedup(nearHistoric.map((x) => x.name).filter(Boolean) as string[]),
        cafes: dedup(nearCafes.map((x) => x.name).filter(Boolean)).slice(0, 3),
        landmarks: dedup(nearLandmarks.map((x) => x.name).filter(Boolean)).slice(0, 3),
        google: googleNames,
      },
      googleWeightedHits,
      osmScore,
      osmHits: dedupKinds(osmHits),
    };
  });

  // ---- Route-level consistency / density ----
  // Per spec: continuous pleasantness matters more than isolated nice points.
  // A segment counts as "pleasant" when its OSM env score is meaningfully
  // positive (≥ +2 ≈ a residential street + something else, OR a single
  // strong feature like a park/water hit). Falls back to Google-only when
  // OSM env data is absent (e.g. fetch failed).
  const pleasantSegs = segments.filter((s) =>
    osmEnv ? s.osmScore >= 2 : s.googleWeightedHits >= 0.5,
  ).length;
  const pleasantSegmentRatio = segments.length > 0 ? pleasantSegs / segments.length : 0;

  // Density: total Google weighted hits per km (kept for headline copy).
  const totalWeighted = segments.reduce((a, s) => a + s.googleWeightedHits, 0);
  const pleasantDensityPerKm = km > 0 ? totalWeighted / km : 0;

  // Distance-weighted average OSM env score.
  const osmEnvScore =
    totalM > 0
      ? segments.reduce((a, s) => a + s.osmScore * s.lengthM, 0) / totalM
      : 0;

  // Distance-weighted fraction on a strongly-negative segment (spec rule #2:
  // "should not be labeled pleasant if much of the path is on highways /
  // industrial / parking corridors").
  const unpleasantM = segments.reduce(
    (a, s) => a + (s.osmScore <= -3 || s.onMajorRoad ? s.lengthM : 0),
    0,
  );
  const unpleasantDistanceRatio = totalM > 0 ? unpleasantM / totalM : 0;

  return {
    route, segments, totalM,
    pleasantSegmentRatio, pleasantDensityPerKm,
    osmEnvScore, unpleasantDistanceRatio,
  };
}

/**
 * Distance falloff for an OSM point hit. 1.0 at distance 0, ~0.25 at the
 * buffer edge (so distant hits still count, but less). Linear interp.
 */
function falloffFactor(d: number, buffer: number): number {
  if (d <= 0) return 1;
  if (d >= buffer) return 0;
  const t = d / buffer;
  return 1 - 0.75 * t; // 1 → 0.25
}

/** Compress a positive lift so isolated big values don't pin a dim to 100. */
function compressLift(v: number): number {
  if (v <= 0) return 0;
  if (v <= 20) return v;
  return 20 + Math.sqrt(v - 20) * 4;
}

function dedupKinds(arr: OsmAreaKind[]): OsmAreaKind[] {
  return Array.from(new Set(arr));
}

function communityBonus(tags: CommunityTag[], seg: LngLat[], buffer: number) {
  let green = 0, calm = 0, stress = 0, lively = 0;
  for (const t of tags) {
    const d = distToSegment([t.lng, t.lat], seg);
    if (d > buffer) continue;
    const w = recencyWeight(t.created_at) * Math.max(0.05, (t as { reporter_weight?: number }).reporter_weight ?? 0.2);
    const label = (t.tag_label ?? "").toLowerCase();
    const sentiment = (t as any).sentiment as string | undefined;
    const points = w * 12;
    if (label.includes("green") || label.includes("park")) green += points;
    if (label.includes("calm") || label.includes("quiet") || label.includes("beautiful")) calm += points;
    if (label.includes("loud") || label.includes("stress") || label.includes("ugly") || label.includes("unsafe")) stress += points;
    if (label.includes("interesting") || label.includes("lively")) lively += points;
    // Public-bathroom availability is a comfort signal — small positive lift,
    // mostly to "calm" (relief = less stress) and a touch of "lively" since
    // amenity-rich stretches tend to be more inhabited.
    if (label.includes("bathroom") || label.includes("restroom") || label.includes("toilet")) {
      calm   += points * 0.4;
      lively += points * 0.2;
    }
    if (sentiment === "negative") stress += points * 0.4;
    if (sentiment === "positive") calm += points * 0.2;
  }
  return { green, calm, stress, lively };
}

function dedup(arr: string[]): string[] {
  return Array.from(new Set(arr));
}

function clamp(n: number, lo = 0, hi = 100): number {
  return Math.max(lo, Math.min(hi, n));
}

// =====================================================================
// Profile weighting + match score
// =====================================================================

// PleasantnessProfile imported at top of file.

interface Weights {
  positive: Partial<Record<Exclude<Dim, "negative">, number>>;
  negativeStrength: number; // 0..1 multiplier on the negative penalty
}

const PROFILE_WEIGHTS: Record<PleasantnessProfile, Weights> = {
  nature: {
    positive: { green: 0.40, quiet: 0.25, blue: 0.15, historic: 0.10, lively: 0.05 },
    negativeStrength: 0.9,
  },
  water: {
    positive: { blue: 0.45, green: 0.20, quiet: 0.15, historic: 0.10, lively: 0.05 },
    negativeStrength: 0.9,
  },
  historic: {
    positive: { historic: 0.45, lively: 0.20, quiet: 0.15, green: 0.10, blue: 0.05 },
    negativeStrength: 0.6,
  },
  quiet: {
    positive: { quiet: 0.45, green: 0.25, blue: 0.10, historic: 0.10, lively: 0.0 },
    negativeStrength: 1.1,
  },
  lively: {
    positive: { lively: 0.35, historic: 0.25, green: 0.10, blue: 0.10, quiet: 0.05 },
    negativeStrength: 0.5,
  },
};

import type { Preference } from "@/utils/scoring";

/**
 * Distance-weighted average of segment scores per dimension.
 * Returns 0..100 for each dimension across the whole route.
 */
export function aggregateRouteDimensions(seg: SegmentedRoute): Record<Dim, number> {
  const total = seg.totalM || 1;
  const sum: Record<Dim, number> = { green: 0, blue: 0, historic: 0, quiet: 0, lively: 0, negative: 0 };
  for (const s of seg.segments) {
    const w = s.lengthM / total;
    for (const k of Object.keys(sum) as Dim[]) sum[k] += s.scores[k] * w;
  }
  return sum;
}

/**
 * Compute the route's match score (0..100) for the user's profile, with
 * small bonuses from existing "loves" toggles. Negative dimension is
 * subtracted, scaled by the profile's negative strength.
 *
 * When `seg` is provided we add a "consistency bonus" — routes whose
 * highly-rated pleasantness is *spread across many segments* score higher
 * than routes with the same total but concentrated in one cluster. This
 * targets the user's stated goal: measure how much of the route *feels*
 * pleasant, not whether something nice exists nearby.
 */
export function profileMatchScore(
  dims: Record<Dim, number>,
  profile: PleasantnessProfile,
  loves: Preference[],
  seg?: SegmentedRoute,
): number {
  const w = PROFILE_WEIGHTS[profile];
  let pos = 0;
  let totalW = 0;
  for (const [k, weight] of Object.entries(w.positive) as [Exclude<Dim, "negative">, number][]) {
    pos += dims[k] * weight;
    totalW += weight;
  }
  if (totalW > 0) pos = pos / totalW; // Normalize to 0..100.

  // Loves bonuses: each matching pref nudges +3 pts (capped at +12).
  const loveBoost = lovesBoost(dims, loves);

  // Consistency bonus (spec rule #1): if pleasant features cover 40%+ of
  // segments, add a route-level bonus. Heavier above 70% (truly continuous).
  let spreadBonus = 0;
  if (seg && profile !== "lively") {
    const ratio = seg.pleasantSegmentRatio; // 0..1
    if (ratio >= 0.7)      spreadBonus += 14;
    else if (ratio >= 0.4) spreadBonus += 8;
    spreadBonus += Math.min(6, seg.pleasantDensityPerKm * 4);
  } else if (seg && profile === "lively") {
    spreadBonus += Math.min(10, seg.pleasantDensityPerKm * 5);
  }

  // OSM environmental bump: distance-weighted env score (signed) folded in
  // at low weight. Caps at ±10 so it nudges, doesn't dominate.
  let envBump = 0;
  if (seg) envBump = Math.max(-10, Math.min(10, seg.osmEnvScore * 2));

  // Hard penalty (spec rule #2): a route should NOT be labeled pleasant if
  // much of the actual path is on highways/major arterials/industrial/
  // parking corridors. Penalty kicks in past 25% and grows linearly.
  let unpleasantPenalty = 0;
  if (seg && seg.unpleasantDistanceRatio > 0.25) {
    unpleasantPenalty = Math.min(35, (seg.unpleasantDistanceRatio - 0.25) * 70);
  }

  const penalty = dims.negative * w.negativeStrength;
  return clamp(pos + loveBoost + spreadBonus + envBump - penalty * 0.6 - unpleasantPenalty);
}

function lovesBoost(dims: Record<Dim, number>, loves: Preference[]): number {
  // Each pref the user explicitly selected meaningfully steers route choice.
  // Positive matches scale with how much of the route delivers on the pref.
  let boost = 0;
  const scaled = (dim: number, threshold: number, max: number) => {
    if (dim <= threshold) return 0;
    return Math.min(max, ((dim - threshold) / (100 - threshold)) * max);
  };
  for (const p of loves) {
    if (p === "parks" || p === "scenic")        boost += scaled(dims.green, 25, 8);
    if (p === "scenic")                         boost += scaled(dims.blue, 25, 10);
    if (p === "historic" || p === "monuments" || p === "architecture")
                                                boost += scaled(dims.historic, 20, 7);
    if (p === "quiet" || p === "no-traffic")    boost += scaled(dims.quiet, 50, 8);
    if (p === "coffee" || p === "shops")        boost += scaled(dims.lively, 30, 6);
    if (p === "lively-streets")                 boost += scaled(dims.lively, 35, 8);
    // Strong penalty when the user explicitly avoids highways but the route
    // still rides a lot of negative (motorway/arterial/industrial) segments.
    if (p === "no-highways" || p === "quiet") {
      if (dims.negative > 20) boost -= Math.min(15, (dims.negative - 20) * 0.5);
    }
  }
  return Math.max(-20, Math.min(25, boost));
}


/**
 * Build human-readable evidence chips for a segmented route.
 * Capped at ~5 chips: leads with the strongest positive evidence and
 * always includes any meaningful penalty.
 */
export function buildEvidence(
  seg: SegmentedRoute,
  profile: PleasantnessProfile,
): string[] {
  const dims = aggregateRouteDimensions(seg);
  const total = seg.totalM;
  // Aug 19 units audit: was hardcoded miles/feet. fmtDistance honours the
  // driver's chosen system. Evidence is baked at scoring time, so a chip
  // reflects the units at generation — acceptable: regenerating the route
  // (which any units change worth arguing about eventually causes) rebakes it.
  const fmtMi = (m: number) => fmtDistance(m);

  // Distance covered by each dimension (segments where score >= threshold).
  const distAt = (key: Exclude<Dim, "negative">, thresh: number) =>
    seg.segments.reduce((acc, s) => acc + (s.scores[key] >= thresh ? s.lengthM : 0), 0);

  const blueDist = distAt("blue", 35);
  const greenDist = distAt("green", 35);
  const historicDist = distAt("historic", 35);
  const quietDist = distAt("quiet", 70);

  // Distinct named features.
  const parks = dedup(seg.segments.flatMap((s) => s.hits.parks)).slice(0, 3);
  const waters = dedup(seg.segments.flatMap((s) => s.hits.water)).slice(0, 3);
  const histPlaces = dedup(seg.segments.flatMap((s) => s.hits.historic)).slice(0, 3);
  const landmarks = dedup(seg.segments.flatMap((s) => s.hits.landmarks)).slice(0, 3);
  const cafes = dedup(seg.segments.flatMap((s) => s.hits.cafes)).slice(0, 3);

  // Negatives: meters on motorway/trunk/primary and visible numbered routes
  // ("high-traffic arterials") versus secondary/tertiary ("local arterials").
  // We deliberately group numbered US/state/provincial routes with highways
  // because stoplight-heavy commercial corridors are exactly what Pleasant
  // mode is supposed to avoid, regardless of region.
  const highTrafficDist = seg.route.highTrafficMeters
    ?? seg.segments.reduce((a, s) => a + (s.onMajorRoad ? s.lengthM : 0), 0);
  const motorwayDist = seg.segments.reduce(
    (a, s) => a + (s.scores.negative >= 65 ? s.lengthM : 0),
    0,
  );

  // Order chips by how strongly they relate to the profile, with a hard
  // override that always shows the top positive of the route.
  const chips: { text: string; weight: number; kind: "good" | "bad" }[] = [];
  const w = PROFILE_WEIGHTS[profile].positive;

  if (blueDist >= total * 0.05 && (waters.length || dims.blue > 30))
    chips.push({
      text: waters[0]
        ? `${fmtMi(blueDist)} along ${waters[0]}`
        : `${fmtMi(blueDist)} along waterfront`,
      weight: (w.blue ?? 0) * 100,
      kind: "good",
    });

  if (parks.length || greenDist >= total * 0.1)
    chips.push({
      text: parks.length === 1
        ? `passes ${parks[0]}`
        : parks.length > 1
          ? `passes ${parks.length} parks`
          : `${fmtMi(greenDist)} near greenery`,
      weight: (w.green ?? 0) * 100,
      kind: "good",
    });

  if (histPlaces.length || landmarks.length || historicDist >= total * 0.05) {
    const name = histPlaces[0] ?? landmarks[0];
    chips.push({
      text: name ? `passes ${name}` : `${fmtMi(historicDist)} of historic blocks`,
      weight: (w.historic ?? 0) * 100,
      kind: "good",
    });
  }

  if (quietDist >= total * 0.4)
    chips.push({
      text: "uses mostly quieter residential streets",
      weight: (w.quiet ?? 0) * 100,
      kind: "good",
    });

  if (cafes.length && (w.lively ?? 0) > 0.05)
    chips.push({
      text: cafes.length === 1 ? `passes ${cafes[0]}` : `${cafes.length} cafés along the way`,
      weight: (w.lively ?? 0) * 100,
      kind: "good",
    });

  // Consistency chip — earned only when pleasantness is *spread* across the
  // route, not concentrated in one cluster. This is the headline signal for
  // the user goal: "how much of the route feels pleasant".
  if (seg.pleasantSegmentRatio >= 0.5) {
    const pct = Math.round(seg.pleasantSegmentRatio * 100);
    chips.push({
      text: `pleasant places along ${pct}% of the route`,
      weight: 95,
      kind: "good",
    });
  } else if (seg.pleasantSegmentRatio >= 0.25) {
    const pct = Math.round(seg.pleasantSegmentRatio * 100);
    chips.push({
      text: `${pct}% of the route near highly-rated places`,
      weight: 70,
      kind: "good",
    });
  }

  // Surface a top-rated Google place name if we have one and haven't already
  // mentioned a park/landmark with the same role.
  const topGoogle = pickTopGoogleHit(seg);
  if (topGoogle) {
    chips.push({
      text: `passes ${topGoogle.name}`,
      weight: 60,
      kind: "good",
    });
  }


  // Negative chips — report PERCENTAGE on high-traffic arterials (motorway
  // + trunk + primary). Miles alone hid the problem when a route bounced
  // between local streets and Saw Mill River Rd.
  const highTrafficPct = total > 0 ? Math.round((highTrafficDist / total) * 100) : 0;
  if (highTrafficPct >= 5) {
    // Distinguish "actual interstate" from "primary/trunk arterial" so the
    // chip is honest about what's on the route.
    const isMostlyInterstate = motorwayDist >= highTrafficDist * 0.6;
    const label = isMostlyInterstate
      ? `${highTrafficPct}% on highway`
      : `${highTrafficPct}% on busy roads`;
    chips.push({
      text: label,
      weight: 200, // pin to top
      kind: "bad",
    });
  }

  // Sort: bad chips first, then by weight desc.
  chips.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "bad" ? -1 : 1;
    return b.weight - a.weight;
  });

  return chips.slice(0, 5).map((c) => c.text);
}

/** Pick the highest-weighted Google hit name across all segments. */
function pickTopGoogleHit(seg: SegmentedRoute): { name: string; type: GooglePlaceType } | null {
  // Prefer parks > tourist_attraction > museum > church > library, and
  // ignore café/restaurant (noisy + weakly pleasant on their own).
  const order: GooglePlaceType[] = ["park", "tourist_attraction", "museum", "church", "library"];
  for (const t of order) {
    for (const s of seg.segments) {
      const hit = s.hits.google.find((g) => g.type === t);
      if (hit) return hit;
    }
  }
  return null;
}

// =====================================================================
// Cheap point extraction from existing OSM result
// =====================================================================

/**
 * Pull lat/lng points out of the raw Overpass response we already fetch
 * for parks/water/historic. Today fetchOsmFeatures returns aggregates only,
 * so we expose this hook for callers that have richer data; otherwise
 * segmentRoute falls back to corridor-level density.
 */
export type OsmPoints = NonNullable<BuildArgs["osmPoints"]>;
